import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const normalizeWorkflowStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "created") return "pending";
  if (normalized === "closed") return "completed";
  if (["revisit", "re visit", "re-visit"].includes(normalized)) return "re-visit";
  if (normalized === "customer waiting for order") return "need to provide quote";
  if (["pending", "re-visit", "completed", "assigned", "in progress", "need to provide quote", "other"].includes(normalized)) {
    return normalized;
  }
  return "pending";
};

const formatWorkflowStatus = (value = "") => {
  const normalized = normalizeWorkflowStatus(value);
  if (normalized === "re-visit") return "Re-Visit";
  if (normalized === "in progress") return "In Progress";
  if (normalized === "customer waiting for order" || normalized === "need to provide quote") return "Need to Provide Quote";
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const workflowBadgeClass = (value = "") => {
  const normalized = normalizeWorkflowStatus(value);
  if (normalized === "completed") return "bg-emerald-100 text-emerald-700 ring-emerald-200";
  if (normalized === "re-visit") return "bg-rose-100 text-rose-700 ring-rose-200";
  if (normalized === "in progress") return "bg-sky-100 text-sky-700 ring-sky-200";
  if (normalized === "assigned") return "bg-violet-100 text-violet-700 ring-violet-200";
  if (normalized === "customer waiting for order" || normalized === "need to provide quote") return "bg-indigo-100 text-indigo-700 ring-indigo-200";
  if (normalized === "other") return "bg-slate-100 text-slate-700 ring-slate-200";
  return "bg-amber-100 text-amber-700 ring-amber-200";
};

const approvalBadgeClass = (value = "") => {
  if (value === "Approved") return "bg-emerald-100 text-emerald-700 ring-emerald-200";
  if (value === "Re-Approved/Locked") return "bg-teal-100 text-teal-700 ring-teal-200";
  if (value === "Edit Access Granted") return "bg-blue-100 text-blue-700 ring-blue-200";
  if (value === "Changes Requested") return "bg-rose-100 text-rose-700 ring-rose-200";
  return "bg-amber-100 text-amber-700 ring-amber-200";
};

const getCertificateSummary = (products = []) =>
  (Array.isArray(products) ? products : [])
    .map((product) => product?.certificateNumber || product?.certificate_number || product?.testCertificateNumber || "")
    .filter(Boolean)
    .join(", ");

export default function Reports() {
  const role = localStorage.getItem("role") || "";
  const canManageReports = role === "admin";
  const isAccountsView = role === "accounts";
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingStatusId, setSavingStatusId] = useState("");
  const [showAllReports, setShowAllReports] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [visitsRes, complaintsRes, customersRes] = await Promise.all([
          axios.get(API_BASE_URL + "/api/visits"),
          axios.get(API_BASE_URL + "/api/complaints"),
          axios.get(API_BASE_URL + "/api/customers")
        ]);

        const visits = Array.isArray(visitsRes.data) ? visitsRes.data : [];
        const complaints = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
        const customers = Array.isArray(customersRes.data) ? customersRes.data : [];

        const customerMap = new Map(customers.map((customer) => [String(customer._id), customer]));
        const visitMap = visits.reduce((map, visit) => {
          [visit.complaintId, visit.complaintRefId]
            .filter(Boolean)
            .forEach((key) => {
              const normalizedKey = String(key);
              const current = map.get(normalizedKey);
              const currentTime = new Date(current?.updatedAt || current?.createdAt || 0).getTime();
              const nextTime = new Date(visit?.updatedAt || visit?.createdAt || 0).getTime();

              if (!current || nextTime >= currentTime) {
                map.set(normalizedKey, visit);
              }
            });

          return map;
        }, new Map());

        const matchedVisitIds = new Set();

        const mappedRows = complaints.map((complaint) => {
          const report =
            visitMap.get(String(complaint._id)) ||
            visitMap.get(String(complaint.complaintId));

          if (report?._id) {
            matchedVisitIds.add(String(report._id));
          }

          const customer = customerMap.get(String(complaint.customerId));
          const approvalStatus = normalizeApprovalStatus(report?.approvalStatus);
          const reportStatus = !report
            ? "Yet to be submitted"
            : isAccountsView && approvalStatus !== "Approved"
            ? "Awaiting admin verification"
            : (normalizeWorkflowStatus(report.finalStatus) || "pending");

          return {
            complaintId: complaint.complaintId,
            complaintDbId: complaint._id,
            company: complaint.company || customer?.name || "-",
            customer: customer?.name || complaint.company || "-",
            engineer: complaint.engineerAssigned || report?.engineer || "-",
            visitDate: complaint.visitDate || report?.visitDate || report?.date || "",
            reportStatus,
            approvalStatus,
            report
          };
        });

        const orphanRows = visits
          .filter((visit) => visit?._id && !matchedVisitIds.has(String(visit._id)))
          .map((visit) => ({
            complaintId: visit.complaintRefId || visit.complaintId || "-",
            complaintDbId: `report-${visit._id}`,
            company: visit.company || visit.customerName || "-",
            customer: visit.customerName || visit.company || "-",
            engineer: visit.engineer || "-",
            visitDate: visit.visitDate || visit.date || "",
            reportStatus:
              isAccountsView && normalizeApprovalStatus(visit.approvalStatus) !== "Approved"
                ? "Awaiting admin verification"
                : (normalizeWorkflowStatus(visit.finalStatus) || "pending"),
            approvalStatus: normalizeApprovalStatus(visit.approvalStatus),
            report: visit
          }));

        const combinedRows = [...mappedRows, ...orphanRows].sort((a, b) => {
          const aTime = new Date(a.report?.updatedAt || a.report?.createdAt || a.visitDate || 0).getTime();
          const bTime = new Date(b.report?.updatedAt || b.report?.createdAt || b.visitDate || 0).getTime();
          return bTime - aTime;
        });

        setRows(
          isAccountsView
            ? combinedRows.filter((row) => !row.report || row.approvalStatus === "Approved")
            : combinedRows
        );
      } catch (error) {
        console.error("Failed to load reports", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [isAccountsView]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      [
        row.complaintId,
        row.customer,
        row.company,
        row.engineer,
        row.reportStatus,
        formatDate(row.visitDate)
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [rows, search]);

  const visibleRows = useMemo(
    () => (showAllReports ? filteredRows : filteredRows.slice(0, 5)),
    [filteredRows, showAllReports]
  );

  const handleStatusChange = async (row, nextStatus) => {
    if (!canManageReports || !row.report?._id) {
      return;
    }

    try {
      setSavingStatusId(row.report._id);

      const response = await axios.put(API_BASE_URL + `/api/visits/${row.report._id}`, {
        finalStatus: normalizeWorkflowStatus(nextStatus)
      });

      const updatedReport = response.data;
      const updatedApprovalStatus = normalizeApprovalStatus(updatedReport?.approvalStatus);

      setRows((current) =>
        current.map((item) =>
          item.complaintDbId === row.complaintDbId
            ? {
                ...item,
                report: updatedReport,
                approvalStatus: updatedApprovalStatus,
                reportStatus:
                  isAccountsView && updatedApprovalStatus !== "Approved"
                    ? "Awaiting admin verification"
                    : (normalizeWorkflowStatus(updatedReport?.finalStatus) || "pending"),
                visitDate: updatedReport?.visitDate || updatedReport?.date || item.visitDate
              }
            : item
        )
      );
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update report status");
    } finally {
      setSavingStatusId("");
    }
  };

  return (
    <div className="space-y-6">
      <section className="theme-panel rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search complaint ID, customer, engineer..."
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
          />
        </div>

        {loading ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-slate-500">
            Loading reports...
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-slate-500">
            No complaints or reports found
          </div>
        ) : (
          <>
            <div className="space-y-4 lg:hidden">
              {visibleRows.map((row) => (
                <article key={row.complaintDbId} className="theme-panel rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,_rgba(248,250,252,0.9),_rgba(255,255,255,1))] p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-base font-bold text-slate-900">{row.complaintId}</p>
                      <p className="mt-1 text-sm text-slate-600">{row.customer}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${approvalBadgeClass(row.approvalStatus)}`}>
                        {row.approvalStatus}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${workflowBadgeClass(row.reportStatus)}`}>
                        {row.report ? formatWorkflowStatus(row.reportStatus) : "Awaiting report"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <ResponsiveField label="Engineer" value={row.engineer} />
                    <ResponsiveField label="Visit Date" value={formatDate(row.visitDate)} />
                    <ResponsiveField label="Certificate No" value={getCertificateSummary(row.report?.products) || "-"} />
                  </div>

                  <div className="theme-panel mt-4 rounded-2xl border border-slate-200 bg-white/90 p-3">
                    {canManageReports && row.report ? (
                      <>
                        <select
                          value={normalizeWorkflowStatus(row.report.finalStatus)}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          disabled={savingStatusId === row.report._id}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <option value="pending">Pending</option>
                          <option value="re-visit">Re-Visit</option>
                          <option value="need to provide quote">Need to provide quote</option>
                          <option value="other">Other</option>
                          <option value="completed">Completed</option>
                        </select>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            onClick={() => navigate(`/reports/${row.report._id}`)}
                            className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/reports/${row.report._id}/edit`)}
                            className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-600"
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    ) : !row.report ? (
                      <div className="flex flex-wrap gap-2">
                        {canManageReports ? (
                          <button
                            onClick={() => navigate(`/reports/create?complaintId=${row.complaintDbId}`)}
                            className="rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                          >
                            Create Report
                          </button>
                        ) : (
                          <span className="inline-flex rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-500">
                            Awaiting report
                          </span>
                        )}
                      </div>
                    ) : row.approvalStatus === "Approved" ? (
                      <button
                        onClick={() => navigate(`/accounts/reports/${row.report._id}`)}
                        className="rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                      >
                        View
                      </button>
                    ) : (
                      <span className="inline-flex rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
                        Ask admin to verify
                      </span>
                    )}
                  </div>
                </article>
              ))}
            </div>

            <div className="theme-panel hidden overflow-x-auto rounded-[26px] border border-slate-200 bg-[linear-gradient(180deg,_rgba(248,250,252,0.9),_rgba(255,255,255,1))] lg:block">
              <div className="min-w-[1000px]">
            <div className="grid grid-cols-[1.1fr_1fr_0.9fr_0.9fr_1.2fr] gap-4 border-b border-slate-200 bg-slate-50/90 px-5 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              <div>Complaint</div>
              <div>Customer</div>
              <div>Engineer</div>
              <div>Visit Date</div>
              <div>Report</div>
            </div>

            <div className="divide-y divide-slate-200">
              {visibleRows.map((row) => (
                <div
                  key={row.complaintDbId}
                  className="grid grid-cols-[1.1fr_1fr_0.9fr_0.9fr_1.2fr] gap-4 px-5 py-4 transition hover:bg-sky-50/40"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900">{row.complaintId}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${approvalBadgeClass(row.approvalStatus)}`}>
                        {row.approvalStatus}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${workflowBadgeClass(row.reportStatus)}`}>
                        {row.report ? formatWorkflowStatus(row.reportStatus) : "Awaiting report"}
                      </span>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{row.customer}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.report?._id ? "Report ready" : "No report yet"}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-700">{row.engineer}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-700">{formatDate(row.visitDate)}</p>
                    <p className="mt-1 text-xs text-slate-500">{getCertificateSummary(row.report?.products) || "No certificate yet"}</p>
                  </div>

                  <div>
                    <div className="theme-panel rounded-2xl border border-slate-200 bg-white/90 p-3">
                      {canManageReports && row.report ? (
                        <>
                          <select
                            value={normalizeWorkflowStatus(row.report.finalStatus)}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            disabled={savingStatusId === row.report._id}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <option value="pending">Pending</option>
                            <option value="re-visit">Re-Visit</option>
                            <option value="need to provide quote">Need to provide quote</option>
                            <option value="other">Other</option>
                            <option value="completed">Completed</option>
                          </select>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              onClick={() => navigate(`/reports/${row.report._id}`)}
                              className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/reports/${row.report._id}/edit`)}
                              className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-amber-600"
                            >
                              Edit
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-slate-500">
                            {savingStatusId === row.report._id ? "Saving status..." : "Update status and open the report from here."}
                          </p>
                        </>
                      ) : !row.report ? (
                        <div className="flex flex-wrap gap-2">
                          {canManageReports ? (
                            <button
                              onClick={() => navigate(`/reports/create?complaintId=${row.complaintDbId}`)}
                              className="rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                            >
                              Create Report
                            </button>
                          ) : (
                            <span className="inline-flex rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-500">
                              Awaiting report
                            </span>
                          )}
                        </div>
                      ) : row.approvalStatus === "Approved" ? (
                        <button
                          onClick={() => navigate(`/accounts/reports/${row.report._id}`)}
                          className="rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                        >
                          View
                        </button>
                      ) : (
                        <span className="inline-flex rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
                          Ask admin to verify
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
            </div>
          </>
        )}
      </section>

      {!loading && filteredRows.length > 5 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowAllReports((current) => !current)}
            className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            {showAllReports ? "View Less" : `View More (${filteredRows.length - 5} more)`}
          </button>
        </div>
      )}
    </div>
  );
}

function ResponsiveField({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}
